import { useCallback, useMemo, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  WIDGETS,
  resolveLayout,
  widgetById,
  type DashboardLayout,
  type WidgetEntry,
} from "../../lib/widgets/registry.js";

/**
 * Phase 13.5 — dashboard widget grid.
 *
 * Two modes:
 *   - View: renders each visible widget in order, no chrome. The user
 *     sees just the widgets, exactly as they did pre-13.5 except now
 *     reorderable.
 *   - Edit: each widget gets a small "drag handle ⋮⋮ + hide ✕"
 *     header strip; hidden widgets surface in the
 *     `<WidgetLibraryDrawer />` for re-adding.
 *
 * `@dnd-kit/sortable` provides keyboard-accessible drag handles out of
 * the box (sortable preset + KeyboardSensor + sortableKeyboardCoordinates).
 *
 * Save-on-action: every reorder / hide / add immediately calls
 * `onChange` with the next layout; the parent commits to the API.
 * No explicit "Save" button — feels lighter for what amounts to a
 * personal home-screen.
 */

interface WidgetGridProps {
  readonly layout: DashboardLayout;
  readonly editing: boolean;
  readonly onChange: (next: DashboardLayout) => void;
}

export function WidgetGrid({ layout, editing, onChange }: WidgetGridProps) {
  const resolved = useMemo(() => resolveLayout(layout), [layout]);
  const visibleEntries = useMemo(
    () =>
      resolved.widgets
        .filter((w) => !w.hidden)
        .slice()
        .sort((a, b) => a.position - b.position),
    [resolved],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      const oldIndex = visibleEntries.findIndex((w) => w.widget_id === active.id);
      const newIndex = visibleEntries.findIndex((w) => w.widget_id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reorderedVisible = arrayMove(visibleEntries, oldIndex, newIndex);
      // Merge back with hidden entries — visible ones get position 0..n
      // contiguous; hidden ones keep their relative order at the end.
      const hidden = resolved.widgets.filter((w) => w.hidden);
      const next: DashboardLayout = {
        widgets: [
          ...reorderedVisible.map((w, i) => ({ ...w, position: i })),
          ...hidden.map((w, i) => ({
            ...w,
            position: reorderedVisible.length + i,
          })),
        ],
      };
      onChange(next);
    },
    [visibleEntries, resolved, onChange],
  );

  const hideWidget = useCallback(
    (widget_id: string) => {
      const next: DashboardLayout = {
        widgets: resolved.widgets.map((w) =>
          w.widget_id === widget_id ? { ...w, hidden: true } : w,
        ),
      };
      onChange(next);
    },
    [resolved, onChange],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={visibleEntries.map((w) => w.widget_id)}
        strategy={verticalListSortingStrategy}
      >
        {visibleEntries.map((entry) => (
          <SortableWidget
            key={entry.widget_id}
            entry={entry}
            editing={editing}
            onHide={() => hideWidget(entry.widget_id)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableWidget({
  entry,
  editing,
  onHide,
}: {
  readonly entry: WidgetEntry;
  readonly editing: boolean;
  readonly onHide: () => void;
}) {
  const spec = widgetById(entry.widget_id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: entry.widget_id });
  if (!spec) return null;
  const Component = spec.Component;
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="mb-6">
      {editing && (
        <div className="mb-2 flex items-center justify-between rounded-md border border-dashed border-[var(--color-border-default)] bg-[var(--color-elevated)] px-3 py-1.5">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="flex items-center gap-2 text-xs font-medium text-[var(--color-muted)] hover:text-[var(--color-text)]"
            aria-label={`Drag ${spec.label}`}
          >
            <DragHandleIcon />
            {spec.label}
          </button>
          <button
            type="button"
            onClick={onHide}
            className="rounded-md px-2 py-1 text-xs font-medium text-[var(--color-muted)] hover:bg-[var(--color-card)] hover:text-[var(--color-danger)]"
            aria-label={`Hide ${spec.label}`}
          >
            ✕ Hide
          </button>
        </div>
      )}
      <Component
        {...(entry.instance_config !== undefined
          ? { instance_config: entry.instance_config }
          : {})}
      />
    </div>
  );
}

function DragHandleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="currentColor"
      aria-hidden
    >
      <circle cx="9" cy="6" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="9" cy="18" r="1.5" />
      <circle cx="15" cy="6" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="15" cy="18" r="1.5" />
    </svg>
  );
}

interface WidgetLibraryDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly layout: DashboardLayout;
  readonly onAdd: (widget_id: string) => void;
  readonly onReset: () => void;
}

/**
 * Slide-in drawer listing every widget that's currently hidden, with
 * an "Add" affordance. Reset wipes the user's dashboard_layout entirely
 * so the next render uses DEFAULT_LAYOUT.
 */
export function WidgetLibraryDrawer({
  open,
  onClose,
  layout,
  onAdd,
  onReset,
}: WidgetLibraryDrawerProps) {
  const [confirmReset, setConfirmReset] = useState(false);
  const resolved = useMemo(() => resolveLayout(layout), [layout]);
  const hiddenIds = useMemo(
    () => new Set(resolved.widgets.filter((w) => w.hidden).map((w) => w.widget_id)),
    [resolved],
  );
  const hiddenSpecs = WIDGETS.filter((w) => hiddenIds.has(w.widget_id));

  if (!open) return null;
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col border-l border-[var(--color-border-default)] bg-[var(--color-surface)] shadow-xl"
        aria-label="Widget library"
      >
        <header className="flex items-center justify-between border-b border-[var(--color-border-default)] px-5 py-4">
          <h2 className="font-display text-lg font-medium text-[var(--color-text)]">
            Add a widget
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-[var(--color-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]"
            aria-label="Close widget library"
          >
            ✕
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-4">
          {hiddenSpecs.length === 0 ? (
            <p className="text-sm text-[var(--color-muted)]">
              Every widget is already on your dashboard. Drag to reorder, or
              hide individual widgets and they'll show up here.
            </p>
          ) : (
            <ul className="space-y-2">
              {hiddenSpecs.map((spec) => (
                <li
                  key={spec.widget_id}
                  className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-card)] p-3"
                >
                  <p className="font-medium text-[var(--color-text)]">{spec.label}</p>
                  <p className="mt-1 text-sm text-[var(--color-muted)]">
                    {spec.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => onAdd(spec.widget_id)}
                    className="mt-2 rounded-md bg-[var(--color-accent)] px-3 py-1 text-xs font-medium text-[var(--color-accent-contrast)]"
                  >
                    + Add to dashboard
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <footer className="border-t border-[var(--color-border-default)] p-4">
          {!confirmReset ? (
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              className="w-full rounded-md border border-[var(--color-border-default)] px-3 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
            >
              Reset to defaults
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-[var(--color-muted)]">
                This restores the default widget order and hides anything
                you've added. Your data isn't touched.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmReset(false)}
                  className="flex-1 rounded-md border border-[var(--color-border-default)] px-3 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onReset();
                    setConfirmReset(false);
                  }}
                  className="flex-1 rounded-md bg-[var(--color-danger)] px-3 py-2 text-sm font-medium text-white"
                >
                  Reset
                </button>
              </div>
            </div>
          )}
        </footer>
      </aside>
    </>
  );
}
